import { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui';
import { getNative, onNative, type NativeCameraEvent } from '../../lib/native';
import { setCrewVoiceAudioActive } from '../../lib/speech';
import { pauseAtcForCrewVoice } from '../conditions/AtcPlayback';
import { crewVoice } from './crewVoice';

/** A request-scoped native screen owns audio/lifecycle while it covers WebKit. No PCM crosses JS. */
export function NativeCrewRadio({ missionId, available }: { missionId: string; available: boolean }) {
  const active = useRef<string | null>(null), restoreAtc = useRef<(() => void) | null>(null);
  const [open, setOpen] = useState(false), [message, setMessage] = useState<string | null>(null);
  const release = () => { restoreAtc.current?.(); restoreAtc.current = null; setCrewVoiceAudioActive(false); };
  useEffect(() => {
    const off = onNative<NativeCameraEvent>('crewRadio', event => {
      if (!event || event.requestId !== active.current || !['closed', 'error'].includes(event.state)) return;
      active.current = null; setOpen(false); release(); setMessage(event.message?.slice(0, 300) || 'Crew radio closed');
    });
    return () => { off(); if (active.current) getNative()?.closeCrewRadio?.(active.current); active.current = null; release(); };
  }, []);
  useEffect(() => { if (!available && active.current) { getNative()?.closeCrewRadio?.(active.current); active.current = null; setOpen(false); release(); } }, [available]);
  const start = () => {
    const native = getNative(); if (!available || active.current || !native?.openCrewRadio) return;
    const requestId = crypto.randomUUID(); active.current = requestId; setOpen(true); setMessage(null);
    crewVoice.leave(); restoreAtc.current = pauseAtcForCrewVoice(); setCrewVoiceAudioActive(true);
    try { native.openCrewRadio({ requestId, missionId }); }
    catch { active.current = null; setOpen(false); release(); setMessage('Crew radio could not open. Try again.'); }
  };
  return <section className="crew-voice" aria-label="Crew voice channel">
    <header><strong>Crew radio</strong><span>Internet audio · foreground only</span></header>
    <p>Talk with this shoot’s crew. Hold to transmit; release stops your microphone.</p>
    <Button size="sm" disabled={!available || open} onClick={start}>{open ? 'Crew radio open' : 'Open crew radio'}</Button>
    {!available && <p role="status">Crew radio is unavailable for ended or archived shoots.</p>}
    {message && <p role="status">{message}</p>}
    <small>ATC and spoken cues pause while the radio is open. Leaving Airview Studio closes this channel. No recording.</small>
  </section>;
}
